import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

interface Vehicle {
  id: string;
  customerId: string;
  registration: string;
  make: string;
  model: string;
  year?: number;
  color?: string;
}

interface VehicleSelectProps {
  customerId: string | null;
  value: string | null;
  onChange: (vehicleId: string | null, vehicle?: Vehicle) => void;
  onCreateNew?: () => void;
}

export function VehicleSelect({ customerId, value, onChange, onCreateNew }: VehicleSelectProps) {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!customerId) {
      setVehicles([]);
      onChange(null);
      return;
    }

    const fetchVehicles = async () => {
      setIsLoading(true);
      try {
        const response = await api.get<Vehicle[]>(`/api/customers/${customerId}/vehicles`);
        setVehicles(response || []);
        // Auto-select if only one vehicle
        if (response?.length === 1) {
          onChange(response[0].id, response[0]);
        }
      } catch (error) {
        console.error('Vehicle fetch error:', error);
        setVehicles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVehicles();
  }, [customerId]);

  if (!customerId) {
    return (
      <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
        {t('booking.selectCustomer')} primero
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div>
      <select
        className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        value={value || ''}
        onChange={(e) => {
          const vehicleId = e.target.value || null;
          const vehicle = vehicleId ? vehicles.find(v => v.id === vehicleId) : undefined;
          onChange(vehicleId, vehicle);
        }}
      >
        <option value="">{t('booking.selectVehicle')}</option>
        {vehicles.map((vehicle) => (
          <option key={vehicle.id} value={vehicle.id}>
            {vehicle.registration} - {vehicle.make} {vehicle.model}
            {vehicle.year ? ` (${vehicle.year})` : ''}
          </option>
        ))}
      </select>

      {vehicles.length === 0 && (
        <p className="mt-1 text-sm text-muted-foreground">
          Este cliente no tiene vehiculos registrados.
        </p>
      )}

      {onCreateNew && (
        <button
          type="button"
          className="mt-2 text-sm text-primary hover:text-primary/80"
          onClick={onCreateNew}
        >
          + {t('vehicle.newVehicle')}
        </button>
      )}
    </div>
  );
}
